import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { useAuth } from '../../../../context/AuthContext'

// Reusable modal: applies tbtv-tags to a batch of selected entities.
// Props:
//   count: number of selected items
//   entityLabel: 'zone' | 'ad' (or any singular noun) — used in copy
//   onClose, onSubmit(tags, mode) — caller handles the per-id loop
export default function BulkTagsModal({ count, entityLabel = 'zone', onClose, onSubmit }) {
  const { api } = useAuth()
  const [allTags, setAllTags] = useState([])
  const [picked, setPicked] = useState([])
  const [tagInput, setTagInput] = useState('')
  const [showSugg, setShowSugg] = useState(false)
  const [mode, setMode] = useState('add') // 'add' = merge with existing; 'replace' = overwrite
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    api('/tags').then(d => setAllTags(d.tags || [])).catch(() => {})
  }, [api])

  function addTag(name) {
    const clean = name.trim().toLowerCase()
    if (!clean || picked.includes(clean)) return
    setPicked(p => [...p, clean])
    setTagInput('')
    setShowSugg(false)
  }
  function removeTag(name) {
    setPicked(p => p.filter(t => t !== name))
  }
  function handleKey(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (tagInput.trim()) addTag(tagInput)
    } else if (e.key === 'Backspace' && !tagInput && picked.length) {
      removeTag(picked[picked.length - 1])
    }
  }

  async function handleSave() {
    if (picked.length === 0) {
      setError('Pick at least one tag.')
      return
    }
    setSaving(true)
    setError('')
    try {
      await onSubmit(picked, mode)
    } catch (e) {
      setError(e.message)
    }
    setSaving(false)
  }

  const suggestions = allTags
    .filter(t => t.toLowerCase().includes(tagInput.toLowerCase()) && !picked.includes(t))
    .slice(0, 8)

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#121212', border: '1px solid #333', borderRadius: 10,
          padding: 24, minWidth: 480, maxWidth: 560,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: '1.2rem' }}>
            Apply tags to {count} {entityLabel}{count === 1 ? '' : 's'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#aaa', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: '0.85rem', color: '#aaa', marginBottom: 4 }}>
            Tags
          </label>
          <div style={{ marginBottom: 8 }}>
            {picked.length === 0 && (
              <span className="muted" style={{ fontSize: '0.85rem' }}>No tags picked</span>
            )}
            {picked.map(t => (
              <span className="tag-chip" key={t}>
                {t}
                <button onClick={() => removeTag(t)} aria-label={`Remove ${t}`}>
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
          <div className="tag-input-wrap">
            <input
              className="tag-input"
              placeholder="Type tag, Enter to add…"
              value={tagInput}
              onChange={e => { setTagInput(e.target.value); setShowSugg(true) }}
              onKeyDown={handleKey}
              onFocus={() => setShowSugg(true)}
              onBlur={() => setTimeout(() => setShowSugg(false), 150)}
            />
            {showSugg && suggestions.length > 0 && (
              <div className="tag-suggestions">
                {suggestions.map(s => (
                  <div key={s} className="tag-suggestion" onMouseDown={() => addTag(s)}>
                    {s}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: '0.85rem', color: '#aaa', marginBottom: 6 }}>
            Mode
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.88rem', marginBottom: 4 }}>
            <input type="radio" checked={mode === 'add'} onChange={() => setMode('add')} />
            <span>Add — merge these tags with each {entityLabel}'s existing tags</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.88rem' }}>
            <input type="radio" checked={mode === 'replace'} onChange={() => setMode('replace')} />
            <span>Replace — overwrite each {entityLabel}'s tags with this list</span>
          </label>
        </div>

        {error && (
          <div className="sponsors-status error" style={{ marginBottom: 12 }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={onClose} className="sponsors-refresh-btn">
            Cancel
          </button>
          <button type="button" disabled={saving} onClick={handleSave} className="btn btn-primary">
            {saving ? 'Applying…' : `Apply to ${count} ${entityLabel}${count === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  )
}
